import { useCallback } from 'react';
import { Api } from 'src/api';
import { FormMediaObject } from 'src/components/Form/S3UploadInput';
import { calculateChunks, S3Uploader } from 'src/services/s3-uploader';
import { useMediaUploadStore } from 'src/lib/services/media-upload-store';
import { useShallow } from 'zustand/react/shallow';
import { useToast } from 'src/components/ui/use-toast';
import { useQueryClient } from '@tanstack/react-query';
// Direct import (not via barrel) to avoid a circular module chain through
// BackgroundUploadOrchestrator -> MediaUploadContext.
import { useBackgroundUploadStore } from 'src/features/requests/background-upload/use-background-upload-store';

const THROTTLE_PROGRESS_UPDATE = 100;

type UseUploadOptions = {
  /**
   * Toast when an upload fails. On by default, since most callers swallow the
   * rethrown error; pass false where the caller reports it itself (the profile
   * avatar), or one failure shows two error toasts.
   */
  notifyOnFailure?: boolean;
};

const shortFileName = (fileName?: string) => {
  const name = fileName || 'Unknown file';
  return name.length > 50 ? name.substring(0, 47) + '...' : name;
};

const useUpload = ({ notifyOnFailure = true }: UseUploadOptions = {}) => {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [isUploading, addUpload, updateProgress, finishUpload, setFailed, getStatus] =
    useMediaUploadStore(
      useShallow(
        ({ isUploading, addUpload, updateProgress, finishUpload, setFailed, getStatus }) => [
          isUploading,
          addUpload,
          updateProgress,
          finishUpload,
          setFailed,
          getStatus,
        ],
      ),
    );

  const generateUpload = useCallback(
    async (model: string, file: File, collection_name?: string): Promise<FormMediaObject> => {
      const data = {
        mime_type: file.type,
        name: file.name,
        size: file.size,
        chunks: calculateChunks(file.size),
        collection_name: collection_name,
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      };

      const multiPartUpload = await Api.media.generateMultipartUpload(model, data);

      return {
        ...multiPartUpload.media,
        upload_id: multiPartUpload.upload_id,
        presigned_urls: multiPartUpload.presigned_urls,
        file,
      };
    },
    [],
  );

  const upload = useCallback(
    async (media: FormMediaObject, controller?: AbortController, onFail?: (error: any) => void) => {
      if (!media?.upload_id) {
        return;
      }

      if (isUploading(media.id)) {
        return;
      }

      addUpload({ id: media.id, name: media.file_name, uploadId: media.upload_id, size: media.file?.size });
      const abortController = controller ?? getStatus(media.id)?.abortController;

      try {
        const S3 = new S3Uploader(abortController);
        const uploadStartTime = Date.now();
        let lastUpdate = uploadStartTime;

        const uploadedData: { [chunk: number]: number } = {};

        const parts = await S3.upload(
          media.presigned_urls!,
          media.file!,
          (data: ProgressEvent, chunkIndex) => {
            uploadedData[chunkIndex] = data.loaded;

            // throttle progress update to avoid massive amount of store updates and re-renders at once.
            if (Date.now() - lastUpdate < THROTTLE_PROGRESS_UPDATE) {
              return;
            }

            const sum = Object.values(uploadedData).reduce((a, b) => a + b, 0);
            const percent = Math.round((sum / media.file!.size) * 100);

            // Calculate elapsed time
            const currentTime = Date.now();
            const elapsedTime = currentTime - uploadStartTime;

            // Calculate upload speed (bytes per millisecond)
            const uploadSpeed = sum / (elapsedTime / 1000);

            // Calculate remaining time in milliseconds
            const remainingBytes = media.file!.size - sum;
            const timeLeft = remainingBytes / uploadSpeed;

            updateProgress(media.id, percent, timeLeft, uploadSpeed);
            lastUpdate = Date.now();
          },
          onFail,
        );

        await Api.media.completeMultipartUpload(
          media.id,
          {
            upload_id: media.upload_id!,
            parts: parts,
          },
          abortController,
        );

        // If this S3 upload is tied to an Uploading request (submitted while
        // the upload was still going), mark its pending entry as attached so
        // the orchestrator can finalize the request to Queued on the next
        // poll. The media is already attached to the request at creation, so
        // no separate attach call is needed here.
        const bgStore = useBackgroundUploadStore.getState();
        for (const [reqId, req] of Object.entries(bgStore.activeRequests)) {
          const match = req.pendingUploads.find(
            (u) => u.mediaId === media.id && u.status === 'uploading',
          );
          if (match) {
            bgStore.markUploadAttached(reqId, match.tempId, media.id);
          }
        }

        // Show success toast for individual file upload — unless another file
        // has failed: only one toast shows at a time (TOAST_LIMIT), so a later
        // success would replace the failure toast before anyone read it.
        const hasFailedUpload = Object.values(useMediaUploadStore.getState().status).some(
          (status) => status.failed,
        );
        if (!hasFailedUpload) {
          toast({
            title: 'Upload complete',
            description: `${shortFileName(media.file_name)} has been uploaded successfully`,
            variant: 'success',
          });
        }
      } catch (e) {
        setFailed(media.id);

        // The orange card alone was easy to miss, so a client could leave
        // believing a file went through when it never reached storage (#2718).
        // An abort is the user removing the card themselves — nothing to report.
        if (notifyOnFailure && !abortController?.signal.aborted) {
          toast({
            title: 'Upload failed',
            description: `${shortFileName(media.file_name)} didn't upload. Please try again.`,
            variant: 'destructive',
          });
        }

        throw e;
      } finally {
        finishUpload(media.id);
        // Refresh storage usage indicator after upload completes
        queryClient.invalidateQueries({ queryKey: ['storage-usage-status'] });
      }
    },
    [
      toast,
      queryClient,
      notifyOnFailure,
      isUploading,
      addUpload,
      updateProgress,
      finishUpload,
      setFailed,
      getStatus,
    ],
  );

  return { generateUpload, upload };
};

export { useUpload };
